import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Heart, Music, Plus, Trash2, PlayCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [playlists, setPlaylists] = useState([]);
  const [hearts, setHearts] = useState({ bands: [], tracks: [] });
  const [loading, setLoading] = useState(true);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch playlists
        const playlistsRes = await fetch(`${API}/playlists/my`, {
          headers: getAuthHeaders(),
          
        });
        if (playlistsRes.ok) {
          setPlaylists(await playlistsRes.json());
        }
        
        // Fetch hearts
        const heartsRes = await fetch(`${API}/hearts/my`, {
          headers: getAuthHeaders(),
          
        });
        if (heartsRes.ok) {
          const data = await heartsRes.json();
          setHearts(data);
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [getAuthHeaders]);

  const createPlaylist = async () => {
    if (!newPlaylistName.trim()) {
      toast.error('Please enter a playlist name');
      return;
    }
    
    try {
      const res = await fetch(`${API}/playlists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({ name: newPlaylistName }),
        
      });
      
      if (res.ok) {
        const playlist = await res.json();
        setPlaylists(prev => [playlist, ...prev]);
        setNewPlaylistName('');
        setCreateDialogOpen(false);
        toast.success('Playlist created!');
      }
    } catch (error) {
      toast.error('Failed to create playlist');
    }
  };

  const deletePlaylist = async (playlistId) => {
    if (!confirm('Delete this playlist?')) return;
    
    try {
      const res = await fetch(`${API}/playlists/${playlistId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        setPlaylists(prev => prev.filter(p => p.playlist_id !== playlistId));
        toast.success('Playlist deleted');
      }
    } catch (error) {
      toast.error('Failed to delete playlist');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary font-accent text-3xl">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="profile-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-accent text-5xl text-foreground mb-2">MY PROFILE</h1>
          <p className="font-mono text-sm text-muted-foreground">
            {user?.email} • {user?.role}
          </p>
        </div>

        <Tabs defaultValue="playlists" className="space-y-8">
          <TabsList className="bg-card border-2 border-border p-1">
            <TabsTrigger 
              value="playlists" 
              className="font-mono uppercase data-[state=active]:bg-primary data-[state=active]:text-black"
              data-testid="playlists-tab"
            >
              Playlists
            </TabsTrigger>
            <TabsTrigger 
              value="liked-bands" 
              className="font-mono uppercase data-[state=active]:bg-primary data-[state=active]:text-black"
              data-testid="liked-bands-tab"
            >
              Liked Bands
            </TabsTrigger>
            <TabsTrigger 
              value="liked-tracks" 
              className="font-mono uppercase data-[state=active]:bg-primary data-[state=active]:text-black"
              data-testid="liked-tracks-tab"
            >
              Liked Tracks
            </TabsTrigger>
          </TabsList>

          {/* Playlists Tab */}
          <TabsContent value="playlists" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-sans text-2xl font-bold">Your Playlists</h2>
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="border-2 border-primary bg-primary text-black hover:bg-transparent hover:text-primary font-mono uppercase"
                    data-testid="create-playlist-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Playlist
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-2 border-border">
                  <DialogHeader>
                    <DialogTitle className="font-sans">Create Playlist</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <Input
                      placeholder="Playlist name"
                      value={newPlaylistName}
                      onChange={(e) => setNewPlaylistName(e.target.value)}
                      className="bg-input border-2 border-border font-mono"
                      data-testid="playlist-name-input"
                    />
                    <Button 
                      onClick={createPlaylist}
                      className="w-full bg-primary text-black font-mono uppercase"
                      data-testid="confirm-create-playlist-btn"
                    >
                      Create
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {playlists.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-border">
                <Music className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="font-mono text-muted-foreground mb-4">No playlists yet</p>
                <Button 
                  onClick={() => setCreateDialogOpen(true)}
                  variant="outline"
                  className="font-mono uppercase"
                >
                  Create Your First Playlist
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                {playlists.map((playlist) => (
                  <div 
                    key={playlist.playlist_id}
                    className="border-2 border-border bg-card p-4 flex items-center justify-between card-brutal hover:border-primary transition-colors cursor-pointer"
                    data-testid={`playlist-${playlist.playlist_id}`}
                    onClick={() => navigate(`/playlist/${playlist.playlist_id}`)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-muted flex items-center justify-center border border-border">
                        <PlayCircle className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-sans font-bold text-foreground">{playlist.name}</h3>
                        <p className="font-mono text-xs text-muted-foreground">
                          {playlist.track_ids?.length || 0} tracks
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button 
                        variant="ghost"
                        size="icon"
                        onClick={() => deletePlaylist(playlist.playlist_id)}
                        className="text-destructive hover:text-destructive/80"
                        data-testid={`delete-playlist-${playlist.playlist_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Liked Bands Tab */}
          <TabsContent value="liked-bands" className="space-y-6">
            <h2 className="font-sans text-2xl font-bold">Liked Bands</h2>
            
            {hearts.bands?.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-border">
                <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="font-mono text-muted-foreground mb-4">No liked bands yet</p>
                <Link to="/browse">
                  <Button variant="outline" className="font-mono uppercase">
                    Browse Bands
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {hearts.bands?.map((band) => (
                  <Link 
                    to={`/band/${band.band_id}`} 
                    key={band.band_id}
                    className="no-underline"
                    data-testid={`liked-band-${band.band_id}`}
                  >
                    <div className="border-2 border-border bg-card overflow-hidden card-brutal">
                      <div className="aspect-video overflow-hidden">
                        {band.image_urls?.[0] ? (
                          <img 
                            src={band.image_urls[0]}
                            alt={band.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center">
                            <span className="font-accent text-3xl text-muted-foreground">
                              {band.name[0]}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-4 border-t-2 border-border">
                        <h3 className="font-sans font-bold text-foreground">{band.name}</h3>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {band.genre_tags?.slice(0, 2).map((tag, i) => (
                            <Badge 
                              key={i}
                              variant="outline"
                              className="font-mono text-[10px] uppercase"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Liked Tracks Tab */}
          <TabsContent value="liked-tracks" className="space-y-6">
            <h2 className="font-sans text-2xl font-bold">Liked Tracks</h2>
            
            {hearts.tracks?.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-border">
                <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="font-mono text-muted-foreground mb-4">No liked tracks yet</p>
                <Link to="/browse">
                  <Button variant="outline" className="font-mono uppercase">
                    Browse Bands
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {hearts.tracks?.map((track) => (
                  <div 
                    key={track.track_id}
                    className="border-2 border-border bg-card p-4 flex items-center justify-between"
                    data-testid={`liked-track-${track.track_id}`}
                  >
                    <div>
                      <h3 className="font-sans font-bold text-foreground">{track.title}</h3>
                      <Badge variant="outline" className="font-mono text-[10px] uppercase mt-1">
                        {track.track_type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 text-secondary font-mono text-sm">
                      <Heart className="w-4 h-4 fill-current" />
                      {track.heart_count}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Profile;
